/**
 * Monaco Editor Component
 *
 * Reusable code/markdown editor using Monaco Editor
 * Supports markdown preview, version control, and GitHub publishing
 */

import React, { useState, useCallback, useMemo } from 'react'
import Editor from '@monaco-editor/react'
import DOMPurify from 'dompurify'

/**
 * Sanitize markdown preview HTML to prevent XSS attacks.
 * User-generated content in preview requires sanitization.
 */
function sanitizePreviewHTML(html: string): string {
  if (typeof window === 'undefined') {
    return ''
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'span', 'div', 'code', 'pre',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'hr', 'img', 'table',
      'thead', 'tbody', 'tr', 'th', 'td'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur'],
  })
}
import { Eye, EyeOff, Save, Download, GitBranch, History } from 'lucide-react'
import { EditorAPI, Draft, DraftVersion } from '../../lib/api-client'

interface MonacoEditorProps {
  draftId?: string
  initialContent?: string
  title?: string
  onSave?: (draft: Draft) => void
  onError?: (error: string) => void
  mode?: 'markdown' | 'code'
  language?: string
  theme?: 'vs-dark' | 'light' | 'vs'
}

export const MonacoEditor: React.FC<MonacoEditorProps> = ({
  draftId,
  initialContent = '',
  title = 'Untitled',
  onSave,
  onError,
  mode = 'markdown',
  language = 'markdown',
  theme = 'vs-dark',
}) => {
  const [content, setContent] = useState(initialContent)
  const [draftTitle, setDraftTitle] = useState(title)
  const [showPreview, setShowPreview] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [versions, setVersions] = useState<DraftVersion[]>([])
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving'>('saved')

  // Handle content changes
  const handleEditorChange = useCallback((value: string | undefined) => {
    if (value !== undefined) {
      setContent(value)
      setSaveStatus('unsaved')
    }
  }, [])

  // Save draft
  const handleSave = async () => {
    if (isSaving) return

    setIsSaving(true)
    setSaveStatus('saving')

    try {
      const draft = await EditorAPI.saveDraft({
        id: draftId,
        title: draftTitle,
        markdown: content,
        json_ast: {}, // Would be populated by markdown parser in production
        tenant_id: 'default',
        user_id: 'current-user',
      })

      setSaveStatus('saved')
      onSave?.(draft)

      // Show success briefly
      setTimeout(() => {
        if (saveStatus === 'saved') {
          setSaveStatus('saved')
        }
      }, 2000)

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Save failed'
      onError?.(errorMsg)
      setSaveStatus('unsaved')
    } finally {
      setIsSaving(false)
    }
  }

  // Load version history
  const handleLoadHistory = async () => {
    if (!draftId) {
      onError?.('No draft ID available')
      return
    }

    try {
      const { versions: historyVersions } = await EditorAPI.getHistory(draftId)
      setVersions(historyVersions)
      setShowVersionHistory(true)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load history'
      onError?.(errorMsg)
    }
  }

  // Revert to version
  const handleRevertToVersion = async (versionId: number) => {
    if (!draftId) return

    try {
      const draft = await EditorAPI.revertToVersion(draftId, versionId)
      setContent(draft.markdown)
      setDraftTitle(draft.title)
      setShowVersionHistory(false)
      setSaveStatus('saved')
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Revert failed'
      onError?.(errorMsg)
    }
  }

  // Export draft
  const handleExport = async (format: 'md' | 'json' | 'zip') => {
    if (!draftId) {
      onError?.('No draft ID available for export')
      return
    }

    try {
      const blob = await EditorAPI.exportDraft(draftId, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${draftTitle}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Export failed'
      onError?.(errorMsg)
    }
  }

  // Publish to GitHub
  const handlePublishToGitHub = async () => {
    if (!draftId) {
      onError?.('No draft ID available for publishing')
      return
    }

    const repo = prompt('Enter repository (owner/repo):')
    if (!repo) return

    const branch = prompt('Enter branch name:', `draft-${Date.now()}`)
    if (!branch) return

    const prTitle = prompt('Enter PR title:', draftTitle)
    if (!prTitle) return

    try {
      const { pr_url } = await EditorAPI.publishToGitHub(draftId, repo, branch, prTitle)
      alert(`Pull request created: ${pr_url}`)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Publish failed'
      onError?.(errorMsg)
    }
  }

  // Sanitized preview content - memoized for performance
  const sanitizedPreviewContent = useMemo(
    () => sanitizePreviewHTML(content.replace(/\n/g, '<br/>')),
    [content]
  )

  // Render markdown preview (basic implementation)
  const renderMarkdownPreview = () => {
    return (
      <div className="prose prose-invert max-w-none p-6">
        <div dangerouslySetInnerHTML={{ __html: sanitizedPreviewContent }} />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            className="px-3 py-1 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Document title"
          />

          <div className="flex items-center space-x-1 text-xs text-gray-400">
            <div
              className={`w-2 h-2 rounded-full ${
                saveStatus === 'saved'
                  ? 'bg-green-500'
                  : saveStatus === 'saving'
                  ? 'bg-yellow-500 animate-pulse'
                  : 'bg-red-500'
              }`}
            />
            <span>
              {saveStatus === 'saved' ? 'Saved' : saveStatus === 'saving' ? 'Saving...' : 'Unsaved'}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {mode === 'markdown' && (
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="p-2 hover:bg-gray-800 rounded transition-colors"
              title={showPreview ? 'Hide Preview' : 'Show Preview'}
            >
              {showPreview ? (
                <EyeOff className="w-4 h-4 text-gray-400" />
              ) : (
                <Eye className="w-4 h-4 text-gray-400" />
              )}
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving || saveStatus === 'saved'}
            className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>Save</span>
          </button>

          <button
            onClick={handleLoadHistory}
            className="p-2 hover:bg-gray-800 rounded transition-colors"
            title="Version History"
          >
            <History className="w-4 h-4 text-gray-400" />
          </button>

          <div className="relative group">
            <button className="p-2 hover:bg-gray-800 rounded transition-colors">
              <Download className="w-4 h-4 text-gray-400" />
            </button>
            <div className="absolute right-0 mt-1 w-32 bg-gray-800 border border-gray-700 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => handleExport('md')}
                className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700"
              >
                Export MD
              </button>
              <button
                onClick={() => handleExport('json')}
                className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700"
              >
                Export JSON
              </button>
              <button
                onClick={() => handleExport('zip')}
                className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700"
              >
                Export ZIP
              </button>
            </div>
          </div>

          <button
            onClick={handlePublishToGitHub}
            className="p-2 hover:bg-gray-800 rounded transition-colors"
            title="Publish to GitHub"
          >
            <GitBranch className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Editor/Preview Split */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor Pane */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} border-r border-gray-800`}>
          <Editor
            height="100%"
            language={language}
            theme={theme}
            value={content}
            onChange={handleEditorChange}
            options={{
              minimap: { enabled: true },
              fontSize: 14,
              wordWrap: 'on',
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              formatOnPaste: true,
              formatOnType: true,
            }}
          />
        </div>

        {/* Preview Pane */}
        {showPreview && mode === 'markdown' && (
          <div className="w-1/2 overflow-auto bg-gray-900">
            {renderMarkdownPreview()}
          </div>
        )}
      </div>

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-800 rounded-lg w-2/3 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Version History</h3>
              <button
                onClick={() => setShowVersionHistory(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="overflow-auto p-4">
              {versions.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No version history available</p>
              ) : (
                <div className="space-y-2">
                  {versions.map((version) => (
                    <div
                      key={version.version_id}
                      className="p-3 bg-gray-800 border border-gray-700 rounded hover:border-blue-500 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">
                            Version {version.version_id}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(version.created_at).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRevertToVersion(version.version_id)}
                          className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
